import { toZonedTime } from 'date-fns-tz';

import { CHEAP_CENTS, EXPENSIVE_CENTS, type PriceBucket } from './config';

const TZ = 'Europe/Helsinki';
const SLOT_MS = 15 * 60 * 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PriceSlot {
  startMs: number;
  endMs: number;
  cents: number; // c/kWh incl. VAT
}

export interface ElectricitySnapshot {
  current: PriceSlot | null;
  trend: 'up' | 'down' | 'flat';
  todayMin: PriceSlot | null;
  todayMax: PriceSlot | null;
  nextHigh: PriceSlot | null;
  nextLow: PriceSlot | null;
  today: PriceSlot[];
  tomorrow: PriceSlot[];
  tomorrowAverageCents: number | null;
  allSlots: PriceSlot[];
}

// ─── Parse ───────────────────────────────────────────────────────────────────

interface SahkotinPrice {
  date: string;
  value: number;
}

/**
 * Validate and normalize the raw sahkotin.fi response into PriceSlot[].
 * Invalid items are silently skipped.
 */
export function parseSahkotinResponse(raw: unknown): PriceSlot[] {
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.prices)) return [];

  const slots: PriceSlot[] = [];
  for (const item of obj.prices as unknown[]) {
    if (!item || typeof item !== 'object') continue;
    const p = item as Partial<SahkotinPrice>;
    if (typeof p.value !== 'number' || !isFinite(p.value)) continue;
    if (typeof p.date !== 'string') continue;
    const ms = Date.parse(p.date);
    if (isNaN(ms)) continue;
    slots.push({ startMs: ms, endMs: ms + SLOT_MS, cents: p.value });
  }

  slots.sort((a, b) => a.startMs - b.startMs);
  return slots;
}

// ─── Day splitting ───────────────────────────────────────────────────────────

/**
 * Get the start of today in Europe/Helsinki as a UTC timestamp.
 */
function startOfHelsinkiDay(nowMs: number, offsetDays: number): number {
  const zoned = toZonedTime(nowMs, TZ);
  const y = zoned.getFullYear();
  const m = zoned.getMonth();
  const d = zoned.getDate() + offsetDays;
  // Create a date at midnight in Helsinki-local coords, then find the UTC equivalent.
  // toZonedTime gives us a Date whose getHours etc. are in Helsinki time.
  // We need to go backwards: midnight Helsinki → UTC.
  const midnightLocal = new Date(y, m, d, 0, 0, 0, 0);
  // The offset between UTC and Helsinki in ms at that point
  const helsinkiAtMidnight = toZonedTime(midnightLocal.getTime(), TZ);
  const diff = midnightLocal.getTime() - helsinkiAtMidnight.getTime();
  return midnightLocal.getTime() + diff;
}

/**
 * Split slots into today and tomorrow based on Europe/Helsinki boundaries.
 */
export function splitByLocalDay(slots: PriceSlot[], nowMs: number): { today: PriceSlot[]; tomorrow: PriceSlot[] } {
  const todayStart = startOfHelsinkiDay(nowMs, 0);
  const tomorrowStart = startOfHelsinkiDay(nowMs, 1);
  const dayAfterStart = startOfHelsinkiDay(nowMs, 2);

  const today = slots.filter((s) => s.startMs >= todayStart && s.startMs < tomorrowStart);
  const tomorrow = slots.filter((s) => s.startMs >= tomorrowStart && s.startMs < dayAfterStart);

  return { today, tomorrow };
}

// ─── Current slot ────────────────────────────────────────────────────────────

export function currentSlot(slots: PriceSlot[], nowMs: number): PriceSlot | null {
  return slots.find((s) => nowMs >= s.startMs && nowMs < s.endMs) ?? null;
}

// ─── Trend ───────────────────────────────────────────────────────────────────

export function trendOf(slots: PriceSlot[], nowMs: number): 'up' | 'down' | 'flat' {
  const idx = slots.findIndex((s) => nowMs >= s.startMs && nowMs < s.endMs);
  if (idx === -1 || idx + 1 >= slots.length) return 'flat';
  const curr = slots[idx]!.cents;
  const next = slots[idx + 1]!.cents;
  if (next > curr + 0.01) return 'up';
  if (next < curr - 0.01) return 'down';
  return 'flat';
}

// ─── Min / max ───────────────────────────────────────────────────────────────

export function minMax(slots: PriceSlot[]): { min: PriceSlot | null; max: PriceSlot | null } {
  if (slots.length === 0) return { min: null, max: null };
  let min = slots[0]!;
  let max = slots[0]!;
  for (const s of slots) {
    if (s.cents < min.cents) min = s;
    if (s.cents > max.cents) max = s;
  }
  return { min, max };
}

// ─── Cheapest window ─────────────────────────────────────────────────────────

export interface CheapestWindowResult {
  window: PriceSlot[];
  averageCents: number;
}

/**
 * Find the cheapest consecutive window of `hours` (1..6) starting from now
 * among the available slots. Only considers slots starting at or after `fromNowMs`.
 * Returns null if there aren't enough remaining slots to fill the window.
 */
export function cheapestWindow(slots: PriceSlot[], fromNowMs: number, hours: number): CheapestWindowResult | null {
  const slotsNeeded = hours * 4; // 4 × 15-min slots per hour
  const future = slots.filter((s) => s.startMs >= fromNowMs);
  if (future.length < slotsNeeded) return null;

  let bestSum = Infinity;
  let bestStart = 0;

  // Initial window sum
  let windowSum = 0;
  for (let i = 0; i < slotsNeeded; i++) {
    windowSum += future[i]!.cents;
  }
  bestSum = windowSum;

  // Slide
  for (let i = 1; i <= future.length - slotsNeeded; i++) {
    windowSum -= future[i - 1]!.cents;
    windowSum += future[i + slotsNeeded - 1]!.cents;
    if (windowSum < bestSum) {
      bestSum = windowSum;
      bestStart = i;
    }
  }

  const windowSlots = future.slice(bestStart, bestStart + slotsNeeded);
  return {
    window: windowSlots,
    averageCents: bestSum / slotsNeeded,
  };
}

// ─── Next high / low ─────────────────────────────────────────────────────────

export function nextHighLow(slots: PriceSlot[], nowMs: number): { nextHigh: PriceSlot | null; nextLow: PriceSlot | null } {
  const future = slots.filter((s) => s.startMs > nowMs);
  if (future.length === 0) return { nextHigh: null, nextLow: null };

  let high = future[0]!;
  let low = future[0]!;
  for (const s of future) {
    if (s.cents > high.cents) high = s;
    if (s.cents < low.cents) low = s;
  }
  return { nextHigh: high, nextLow: low };
}

// ─── Bucket ──────────────────────────────────────────────────────────────────

export function bucketFor(cents: number): PriceBucket {
  if (cents <= CHEAP_CENTS) return 'cheap';
  if (cents >= EXPENSIVE_CENTS) return 'expensive';
  return 'normal';
}

// ─── Snapshot builder ────────────────────────────────────────────────────────

export function buildSnapshot(slots: PriceSlot[], nowMs: number): ElectricitySnapshot {
  const { today, tomorrow } = splitByLocalDay(slots, nowMs);
  const curr = currentSlot(slots, nowMs);
  const trend = trendOf(slots, nowMs);
  const { min: todayMin, max: todayMax } = minMax(today);
  const { nextHigh, nextLow } = nextHighLow(slots, nowMs);

  const tomorrowAverageCents = tomorrow.length > 0 ? tomorrow.reduce((sum, s) => sum + s.cents, 0) / tomorrow.length : null;

  return {
    current: curr,
    trend,
    todayMin,
    todayMax,
    nextHigh,
    nextLow,
    today,
    tomorrow,
    tomorrowAverageCents,
    allSlots: slots,
  };
}

// ─── Hourly aggregation (for the tile face) ──────────────────────────────────

export interface HourPrice {
  hour: number; // 0..23 local hour
  startMs: number;
  cents: number; // average of the quarter-slots in this hour
  isPast: boolean;
  isCurrent: boolean;
}

/**
 * Aggregate one local day's 15-minute slots into 24 hourly buckets (averaging
 * the quarters), tagging past/current hours relative to `nowMs`.
 */
export function aggregateDayToHourly(daySlots: PriceSlot[], nowMs: number): HourPrice[] {
  const byHour = new Map<number, { sum: number; count: number; startMs: number }>();
  for (const slot of daySlots) {
    const hour = toZonedTime(slot.startMs, TZ).getHours();
    const entry = byHour.get(hour);
    if (entry) {
      entry.sum += slot.cents;
      entry.count += 1;
      if (slot.startMs < entry.startMs) entry.startMs = slot.startMs;
    } else {
      byHour.set(hour, { sum: slot.cents, count: 1, startMs: slot.startMs });
    }
  }

  const hours: HourPrice[] = [];
  for (const [hour, { sum, count, startMs }] of byHour) {
    const endMs = startMs + count * SLOT_MS;
    hours.push({
      hour,
      startMs,
      cents: sum / count,
      isPast: endMs <= nowMs,
      isCurrent: nowMs >= startMs && nowMs < endMs,
    });
  }
  hours.sort((a, b) => a.hour - b.hour);
  return hours;
}

// ─── Day-scoped cheapest window (for the green underline) ─────────────────────

export interface CheapestHourWindow {
  startHour: number;
  endHour: number; // inclusive
  avgCents: number;
}

/**
 * Cheapest consecutive `windowHours`-hour window within a day's hourly buckets,
 * considering only hours at/after `minStartHour`. Returns null if there aren't
 * enough consecutive eligible hours.
 */
export function cheapestHourWindowInDay(hours: HourPrice[], windowHours: number, minStartHour = 0): CheapestHourWindow | null {
  const eligible = hours.filter((h) => h.hour >= minStartHour).sort((a, b) => a.hour - b.hour);
  if (windowHours < 1 || eligible.length < windowHours) return null;

  let best: CheapestHourWindow | null = null;
  for (let i = 0; i + windowHours <= eligible.length; i++) {
    const slice = eligible.slice(i, i + windowHours);
    const contiguous = slice.every((h, idx) => idx === 0 || h.hour === slice[idx - 1]!.hour + 1);
    if (!contiguous) continue;
    const avg = slice.reduce((sum, h) => sum + h.cents, 0) / windowHours;
    if (!best || avg < best.avgCents) {
      best = { startHour: slice[0]!.hour, endHour: slice[windowHours - 1]!.hour, avgCents: avg };
    }
  }
  return best;
}
